/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
/* eslint-disable sonarjs/cognitive-complexity */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, InputAdornment, TextField } from '@mui/material';
import { isEmpty } from 'lodash';

import { IScheduleServiceProps } from './CreateSchedule.Model';
import { IServiceCategoryItem, IServiceItem } from '../../../../Jobs/Models/AddEditOneTimeJob.Model';

import { BoxShadow } from '../../../../../Shared/Constants/Style.Constants';

import MasterDataApiService from '../../../../../Shared/Services/MasterDataService';

import MultiSelectDropdownWithCheckboxes from '../../../../../Shared/Components/Common/MultiSelectDropdownWithCheckboxes/MultiSelectDropdownWithCheckboxes';

import theme from '../../../../../Shared/Themes/theme';
import { Icons } from '../../../../../Shared/Constants/Icons';
import {
  buttonStyles,
  ErrorMsgWrapper,
  ServiceFieldWrapper,
} from '../../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import { Options } from '../../../../../Shared/Components/Common/Dropdown/Dropdown.styles';
import { FLOW_TYPES } from '../../../../../Shared/Constants/App';

const AWAITING_CUSTOMER_OPTION = 'awaiting-customer';

const ScheduleServices: React.FC<IScheduleServiceProps> = ({
  service,
  setService,
  serviceTasks,
  setServiceTasks,
  customerId,
  isServiceRequest,
}) => {
  const [isServiceLoading, setIsServiceLoading] = useState(false);
  const [isSelectOpen, setSelectOpen] = useState(new Array(service?.length).fill(false));
  const [serviceOptions, setServiceOptions] = useState<IServiceCategoryItem[]>([]);
  const [selectedServices, setSelectedServices] = useState<any[]>([]);
  const { serviceAndAreaSpecification, textFieldService } = buttonStyles;

  const { t } = useTranslation(['jobManagement']);

  // Service categories are scoped to a customer. In the service request flow the customer is chosen on
  // screen, so the field stays disabled and empty until that selection is made.
  const isAwaitingCustomerSelection = !!isServiceRequest && isEmpty(customerId);

  const getServiceCategories = async () => {
    try {
      setIsServiceLoading(true);
      console.log('customerId', customerId, 'isServiceRequest', isServiceRequest);
      const allServiceCategoryV2Response = isServiceRequest
        ? await MasterDataApiService.getServiceCategory(customerId ?? 'ORG_ID', FLOW_TYPES.serviceRequest, 200)
        : await MasterDataApiService.getAllServiceCategoryV3(customerId);
      if (allServiceCategoryV2Response?.data) {
        const modifiedCategories = allServiceCategoryV2Response?.data?.map(
          (categoryItem: { serviceCategoryId?: string; serviceCategoryName?: string; unit: string }) => {
            return {
              categoryId: categoryItem?.serviceCategoryId,
              categoryName: categoryItem?.serviceCategoryName,
              unit: categoryItem?.unit,
            };
          }
        );
        const uniqueCategories: IServiceCategoryItem[] = [
          ...new Map(modifiedCategories?.map((item: any) => [item?.categoryId, item])).values(),
        ] as IServiceCategoryItem[];
        uniqueCategories.sort((a: any, b: any) => a?.categoryName?.localeCompare(b?.categoryName));
        setServiceOptions(uniqueCategories);
        fetchTaskDetails(allServiceCategoryV2Response);
      }
      setIsServiceLoading(false);
    } catch (error) {
      console.error(t('jobManagement:errorFetchingServiceCategories'), error);
    }
  };

  const fetchTaskDetails = (allServiceCategoryV2Response: any) => {
    const uniqueTaskData = allServiceCategoryV2Response?.data?.reduce((acc: any, task: any) => {
      const { serviceCategoryId } = task;
      if (!acc[serviceCategoryId]) {
        acc[serviceCategoryId] = [];
      }
      acc[serviceCategoryId]?.push(task);
      return acc;
    }, {});
    setServiceTasks?.(uniqueTaskData);
  };

  const handleServiceSelectChange = useCallback(
    (selectedServiceId: string, value: string, prevSelectedService: any) => {
      const selectedService = serviceOptions?.find((serviceItem) => serviceItem?.categoryId === value);
      const modifiedServiceOptions = service?.map((serviceItem) => {
        if (serviceItem.id === selectedServiceId) {
          serviceItem.service = value ?? '';
          serviceItem.mdServiceId = selectedService?.categoryId ?? '';
          serviceItem.unit = selectedService?.unit ?? '';
          serviceItem.nameError = '';
          serviceItem.name = selectedService?.categoryName;
        }
        return serviceItem;
      });

      if (!selectedServices.includes(selectedService?.categoryId)) {
        setSelectedServices((prevState) => {
          if (!prevState.includes(selectedService?.categoryId)) {
            const filterNonEmptyService = service?.filter((item) => item?.service !== '');
            if (selectedServices?.length >= filterNonEmptyService?.length) {
              const filterPrevState = [...prevState]?.filter((serviceId) => serviceId !== prevSelectedService);
              return [...filterPrevState, selectedService?.categoryId];
            }
            return [...prevState, selectedService?.categoryId];
          }
          return [];
        });
      }
      setService(modifiedServiceOptions);
    },
    [service, serviceOptions]
  );

  const handleSelectedTasks = (selectedTasks: string[], selectedServiceId: string) => {
    const modifiedServiceOptions = service?.map((serviceItem: IServiceItem) => {
      if (serviceItem?.mdServiceId === selectedServiceId) {
        serviceItem.tasks = isEmpty(selectedTasks) ? '' : selectedTasks?.join(', ');
        serviceItem.tasksError = '';
      }
      return serviceItem;
    });
    setService(modifiedServiceOptions);
  };

  const toggleSelectOpen = (index: number) => {
    const updatedIsSelectOpen = [...isSelectOpen];
    updatedIsSelectOpen[index] = !updatedIsSelectOpen[index];
    setSelectOpen(updatedIsSelectOpen);
  };

  const renderSelectTasksField = (mdServiceId: string, selectedService: IServiceItem) => {
    return (
      <>
        {!isEmpty(selectedServices) && selectedServices.includes(mdServiceId) && (
          <MultiSelectDropdownWithCheckboxes
            label={t('oneTimeJob:selectTasks')}
            isMandatory={true}
            dropdownValues={serviceTasks[mdServiceId]}
            defaultValues={selectedService?.tasks ? selectedService.tasks.split(', ') : []}
            error={selectedService?.tasksError}
            onSelect={(data: string[]) => handleSelectedTasks(data, mdServiceId)}
            showSelectAll={false}
          />
        )}
      </>
    );
  };

  useEffect(() => {
    // In the service request flow a multi-customer vendor picks the customer after this component has
    // mounted, so the categories have to be re-fetched every time that selection changes.
    if (isAwaitingCustomerSelection) {
      setServiceOptions([]);
      return;
    }
    setSelectedServices([]);
    getServiceCategories();
  }, [customerId]);

  return (
    <>
      {service?.map((serviceItem, index) => {
        const prevSelectedService = serviceItem?.service;

        return (
          <Box key={serviceItem?.id} sx={{ ...serviceAndAreaSpecification, flexWrap: 'wrap', rowGap: '2rem' }}>
            <ServiceFieldWrapper>
              <TextField
                select
                sx={{
                  pointerEvents: isServiceLoading ? 'none' : 'all',
                  ...textFieldService,
                }}
                label={
                  <span>
                    {t('oneTimeJob:selectService')} <span className="mandatory-field">*</span>
                  </span>
                }
                value={serviceItem?.mdServiceId}
                onChange={(e) => handleServiceSelectChange(serviceItem?.id, e?.target?.value, prevSelectedService)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {index === 0 && isServiceLoading ? (
                        <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
                      ) : null}
                    </InputAdornment>
                  ),
                }}
                SelectProps={{
                  IconComponent: () =>
                    !isServiceLoading ? (
                      <button
                        onClick={() => toggleSelectOpen(index)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          padding: 0,
                        }}>
                        <img
                          src={isSelectOpen[index] ? Icons.DropdownDownIcon : Icons.DropdownUpIcon}
                          alt={isSelectOpen[index] ? 'down' : 'up'}
                          onClick={() => toggleSelectOpen(index)}
                        />
                      </button>
                    ) : null,
                  open: isSelectOpen[index],
                  onOpen: () => toggleSelectOpen(index),
                  onClose: () => toggleSelectOpen(index),
                  MenuProps: {
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'center',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'center',
                    },
                    PaperProps: {
                      style: {
                        boxShadow: BoxShadow.BoxShadow,
                      },
                    },
                  },
                }}>
                {isAwaitingCustomerSelection ? (
                  // A non-empty value keeps this out of range of the field's own value, so it renders
                  // only inside the menu and never as the selected text.
                  <Options disabled value={AWAITING_CUSTOMER_OPTION}>
                    {t('serviceRequests:selectCustomerFirst')}
                  </Options>
                ) : (
                  serviceOptions?.map((item: any) => {
                    const alreadySelected = selectedServices?.includes(item?.categoryId);

                    return (
                      <Options
                        style={{
                          opacity: `${alreadySelected ? 0.3 : ''}`,
                          pointerEvents: `${alreadySelected ? 'none' : 'auto'}`,
                        }}
                        key={item?.categoryId}
                        value={item?.categoryId}>
                        {item?.categoryName}
                      </Options>
                    );
                  })
                )}
              </TextField>
              <ErrorMsgWrapper>{serviceItem?.nameError}</ErrorMsgWrapper>
            </ServiceFieldWrapper>

            {renderSelectTasksField(serviceItem?.mdServiceId, serviceItem)}
          </Box>
        );
      })}
    </>
  );
};

export default ScheduleServices;
